const TIME_COLON_PATTERN = /^(\d{1,2}):(\d{1,2})$/;
const MAX_TYPED_DIGITS = 4;

export function normalizeTypedTimeInput(rawInput: string): string | null {
  const value = (rawInput || '').trim();
  if (!value) return null;

  const colonMatch = value.match(TIME_COLON_PATTERN);
  if (colonMatch) {
    return toTimeString(Number(colonMatch[1]), Number(colonMatch[2]));
  }

  const digits = value.replace(/\D/g, '');
  if (!digits) return null;

  if (digits.length <= 2) {
    return toTimeString(Number(digits), 0);
  }

  // 3 digits = single-digit hour + 2-digit minute (e.g. 930 -> 09:30); out-of-range
  // values (e.g. 175 -> 1:75) are rejected by toTimeString and return null.
  if (digits.length === 3) {
    return toTimeString(Number(digits.slice(0, 1)), Number(digits.slice(1)));
  }

  if (digits.length === 4) {
    return toTimeString(Number(digits.slice(0, 2)), Number(digits.slice(2)));
  }

  return null;
}

/** Live display while entering digits (e.g. 7 → 7, 730 → 7:30, 0930 → 09:30). */
export function formatPartialTypedTime(digits: string): string {
  const d = (digits || '').replace(/\D/g, '').slice(0, MAX_TYPED_DIGITS);
  if (!d) return '';
  if (d.length <= 2) return d;
  if (d.length === 3) return `${d.slice(0, 1)}:${d.slice(1)}`;
  return `${d.slice(0, 2)}:${d.slice(2)}`;
}

export function appendTypedTimeDigit(currentDigits: string, digit: string): string {
  return `${currentDigits}${digit}`.replace(/\D/g, '').slice(0, MAX_TYPED_DIGITS);
}

export function extractTimeDigits(value: string): string {
  return (value || '').replace(/\D/g, '').slice(0, MAX_TYPED_DIGITS);
}

export function shouldUseTypedTimeInput(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches === true;
  const touchPoints = navigator.maxTouchPoints ?? 0;
  return coarsePointer || touchPoints > 0;
}

function toTimeString(hours: number, minutes: number): string | null {
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}
